import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline } from '@mui/material';
import { useAuth } from './models/AuthContext';
import { ChatProvider, useChat } from './models/ChatContext';
import Home from './views/Home';
import Quotes from './views/Quotes';
import Invoices from './views/Invoices';
import Customers from './views/Customers';
import Login from './views/Login';
import HUE from './views/HUE';
import Charges from './views/charges';
import SpeechToSpeech from './views/chat';
import TranscriptView from './views/TranscriptView';
import GoogleAuth from './views/GoogleAuth';
import FloatingChatButton from './components/FloatingChatButton';
import ChatOverlay from './components/ChatOverlay';
import Trimestres from './views/Trimestres';
import Rain from './views/Rain';
import NetatmoCallback from './components/NetatmoCallback';
import AdminNetatmo from './views/AdminNetatmo';
import AdminAutomower from './views/AdminAutomower';
import Automower from './views/Automower';
import { signOut } from 'firebase/auth';
import { auth } from './config/firebase';

import Rail, { NAV } from './components/design/Rail';
import MobileHub from './components/design/MobileHub';
import { Ic } from './components/design/Icons';
import { ThemeProvider, useTheme } from './components/design/ThemeContext';
import './styles/theapp-design.css';
import './App.css';

const AppContent = () => {
  const { currentUser } = useAuth();
  const { toggleChatVisibility, chatActive } = useChat();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileHubOpen, setMobileHubOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('theapp-design');
    return () => document.body.classList.remove('theapp-design');
  }, []);

  useEffect(() => {
    setMobileHubOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('authToken');
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const isLoginPage = location.pathname === '/login';
  const shouldShowNavigation = !isLoginPage && !!currentUser;

  // derive current section name from path for breadcrumb
  const currentNav = NAV.find((n) => {
    if (n.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(n.path);
  });
  const sectionLabel = currentNav ? currentNav.id : 'app';

  if (!shouldShowNavigation) {
    return (
      <Routes>
        <Route path="/login" element={currentUser ? <Navigate to="/" /> : <Login />} />
        <Route path="/rain/callback" element={<NetatmoCallback />} />
        <Route path="/GoogleAuth" element={<GoogleAuth />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <div className="ta-app">
      <Rail onLogout={handleLogout} />

      <main className="ta-main">
        {/* Desktop top bar */}
        <header className="ta-top">
          <div className="ta-crumb">
            <span className="root">theapp</span>
            <span className="sep">/</span>
            <span className="section">{sectionLabel}</span>
          </div>
          <div className="ta-top-right">
            <button className="ta-icon-btn" aria-label="Recherche">{Ic.search}</button>
            <div className="ta-top-div" />
            <button
              className="ta-icon-btn"
              aria-label={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            >
              {theme === 'dark' ? Ic.sun : Ic.moon}
            </button>
            <div className="ta-top-div" />
            <button
              className="ta-icon-btn"
              aria-label="Assistant"
              onClick={toggleChatVisibility}
              style={chatActive ? { color: 'var(--ok)', borderColor: 'color-mix(in oklch, var(--ok), transparent 60%)' } : {}}
            >
              {Ic.chat}
            </button>
            <div className="ta-top-div" />
            <button className="ta-icon-btn" aria-label="Compte" onClick={handleLogout}>
              {Ic.user}
            </button>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="ta-mobile-top">
          <button className="ta-mobile-menu" onClick={() => setMobileHubOpen(true)} aria-label="Menu">
            {Ic.menu}
          </button>
          <div className="crumb">{currentNav ? `${currentNav.n} · ${currentNav.label}` : 'THEAPP'}</div>
          <button className="ta-mobile-menu" onClick={toggleChatVisibility} aria-label="Assistant">
            {Ic.chat}
          </button>
        </header>

        <Routes>
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
          <Route path="/charges" element={<ProtectedRoute><Charges /></ProtectedRoute>} />
          <Route path="/trimestres" element={<ProtectedRoute><Trimestres /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
          <Route path="/rain" element={<ProtectedRoute><Rain /></ProtectedRoute>} />
          <Route path="/rain/callback" element={<NetatmoCallback />} />
          <Route path="/automower" element={<ProtectedRoute><Automower /></ProtectedRoute>} />
          <Route path="/admin/netatmo" element={<ProtectedRoute><AdminNetatmo /></ProtectedRoute>} />
          <Route path="/admin/automower" element={<ProtectedRoute><AdminAutomower /></ProtectedRoute>} />
          <Route path="/hue" element={<ProtectedRoute><HUE /></ProtectedRoute>} />
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="/chat" element={<ProtectedRoute><SpeechToSpeech /></ProtectedRoute>} />
          <Route path="/transcript" element={<ProtectedRoute><TranscriptView /></ProtectedRoute>} />
          <Route path="/GoogleAuth" element={<GoogleAuth />} />
        </Routes>
      </main>

      <MobileHub
        open={mobileHubOpen}
        onClose={() => setMobileHubOpen(false)}
        onLogout={handleLogout}
      />
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ChatProvider>
          <Router>
            <CssBaseline />
            <AppContent />
            <FloatingChatButton />
            <ChatOverlay />
          </Router>
        </ChatProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) {
      const redirectUrl = location.pathname + location.search;
      localStorage.setItem('redirectAfterLogin', redirectUrl);
    }
  }, [currentUser, location]);

  const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
  return currentUser || redirectAfterLogin ? children : <Navigate to="/login" />;
};

export default App;
